import { DEAIVisionWebClient } from '@akavirtual/deai-web-core';
import { cn } from '@/lib/utils';
import { useDEAI3DScene } from '@/hooks/use-deai-3dscene';

// Define component props
type DEAI3DSceneProps = {
    placeholderImgSrc: string;
    vrmUrl: string;
    vrmaUrl: string;
    vrmaLibRoot: string;
    vrmaLibConfig?: string;
    animatorLibRoot: string;
    animatorLibConfig?: string;
    cameraPosition?: { x: number; y: number; z: number };
    cameraRotationEuler?: { x: number; y: number; z: number };
    phonemeMultipliers?: Map<string, number>;
    className?: string;
} & React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export const DEAI3DScene = ({ className,
    placeholderImgSrc,
    vrmUrl,
    vrmaUrl,
    vrmaLibRoot,
    vrmaLibConfig = "AnimationConfig.json",
    animatorLibRoot,
    animatorLibConfig = "AnimatorConfig.json",
    cameraPosition,
    cameraRotationEuler,
    phonemeMultipliers
 }: DEAI3DSceneProps) => {
  // Call the hook to get all logic, state, and refs
  const { deaiSceneRef, isModelLoaded, onModelInitialized } = useDEAI3DScene({ phonemeMultipliers });

  return (
    <div className={cn("pointer-events-none h-full w-full", className)}>
      {/* Loading placeholder image */}
      <img
        src={placeholderImgSrc}
        alt="Avatar loading placeholder"
        className={cn(
          "absolute bottom-0 left-1/2 -translate-x-1/2 max-w-[260px]",
          "transition-opacity duration-[1300ms] ease-out",
          isModelLoaded ? "opacity-0" : "opacity-100" // Controlled by the hook's state
        )}
      />
      {/* The 3D scene client */}
      <DEAIVisionWebClient
        className={cn(
          "transition-opacity duration-[1300ms] ease-out",
          isModelLoaded ? "opacity-100" : "opacity-0" // Controlled by the hook's state
        )}
        vrmUrl={vrmUrl}
        vrmaUrl={vrmaUrl}
        vrmaLibRoot={vrmaLibRoot}
        vrmaLibConfig={vrmaLibConfig}
        animatorLibRoot={animatorLibRoot}
        animatorLibConfig={animatorLibConfig}
        debugDragNDrop={true}
        initCameraPosition={cameraPosition || {
          x: -0, y: 1.25, z: -2.5
        }}
        initCameraRotationEuler={cameraRotationEuler}
        onInitialized={onModelInitialized} // Use the callback from the hook
        ref={deaiSceneRef} // Attach the ref from the hook
      />
    </div>
  );
};